import { useState } from "react";
import { useCanAccess, useTranslate } from "ra-core";
import { Link, matchPath, useLocation, useMatch } from "react-router";
import {
  Building2,
  FolderKanban,
  Handshake,
  Home,
  LifeBuoy,
  ListTodo,
  MoreHorizontal,
  Plus,
  Settings,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

import { orderFor, profileFor } from "./navigationOrder";

import { ContactCreateSheet } from "../contacts/ContactCreateSheet";
import { NoteCreateSheet } from "../notes/NoteCreateSheet";
import { TaskCreateSheet } from "../tasks/TaskCreateSheet";

/** How many tabs fit on a phone before they stop being tappable. */
const MAX_TABS = 4;

type NavItem = {
  href: string;
  label: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  visible: boolean;
};

/**
 * The bottom bar.
 *
 * Which destinations exist depends on the viewer's role, so the bar is built
 * from what they can reach rather than from a fixed list -- an admin has seven,
 * a developer three. Four fit comfortably at phone widths; the rest go behind
 * "More" instead of being squeezed to the point of mistapping.
 *
 * Create is a floating button rather than a tab. It is an action, not a place,
 * and mixing it into the tab row was what pushed the row over its budget.
 *
 * The order is by profile rather than fixed, because the roles do different
 * jobs: marketing lives in the lead pipeline, a developer in tasks and tickets,
 * a client in their one project. See navigationOrder.
 */
export const MobileNavigation = () => {
  const location = useLocation();
  const translate = useTranslate();
  const [moreOpen, setMoreOpen] = useState(false);

  // A fixed set of checks, in priority order. Hooks cannot be called in a loop
  // over a filtered list, so every one runs and `visible` decides the rest.
  const dashboard = useCanAccess({ resource: "dashboard", action: "list" });
  const projects = useCanAccess({ resource: "projects", action: "list" });
  const tasks = useCanAccess({ resource: "tasks", action: "list" });
  const tickets = useCanAccess({ resource: "tickets", action: "list" });
  const contacts = useCanAccess({ resource: "contacts", action: "list" });
  const companies = useCanAccess({ resource: "companies", action: "list" });
  const deals = useCanAccess({ resource: "deals", action: "list" });

  // Both mobile screens exist now, so these are real destinations rather than
  // tabs that would land on Not Found.
  const assign = useCanAccess({ resource: "projects", action: "assign" });
  const users = useCanAccess({ resource: "sales", action: "list" });
  const leadsCreate = useCanAccess({ resource: "deals", action: "create" });

  const profile = profileFor((permission) => {
    switch (permission) {
      case "users.list":
        return users.canAccess === true;
      case "projects.assign":
        return assign.canAccess === true;
      case "leads.create":
        return leadsCreate.canAccess === true;
      case "tasks.list":
        return tasks.canAccess === true;
      case "contacts.list":
        return contacts.canAccess === true;
      case "projects.list":
        return projects.canAccess === true;
      default:
        return false;
    }
  });

  const items: NavItem[] = [
    {
      href: "/",
      label: translate("ra.page.dashboard"),
      Icon: Home,
      visible: dashboard.canAccess === true,
    },
    {
      href: "/projects",
      label: translate("resources.projects.name", {
        smart_count: 2,
        _: "Projects",
      }),
      Icon: FolderKanban,
      visible: projects.canAccess === true,
    },
    {
      href: "/tasks",
      label: translate("resources.tasks.name", { smart_count: 2 }),
      Icon: ListTodo,
      visible: tasks.canAccess === true,
    },
    {
      href: "/tickets",
      label: translate("resources.tickets.name", {
        smart_count: 2,
        _: "Tickets",
      }),
      Icon: LifeBuoy,
      visible: tickets.canAccess === true,
    },
    {
      href: "/contacts",
      label: translate("resources.contacts.name", { smart_count: 2 }),
      Icon: Users,
      visible: contacts.canAccess === true,
    },
    {
      href: "/deals",
      label: translate("resources.deals.name", { smart_count: 2 }),
      Icon: Handshake,
      visible: deals.canAccess === true,
    },
    {
      href: "/companies",
      label: translate("resources.companies.name", { smart_count: 2 }),
      Icon: Building2,
      visible: companies.canAccess === true,
    },
  ].filter((item) => item.visible);

  // Settings is always reachable and always last, so it lives in More whenever
  // anything else is competing for a slot.
  const settings: NavItem = {
    href: "/settings",
    label: translate("crm.settings.title"),
    Icon: Settings,
    visible: true,
  };

  // The same arrangement the desktop header uses, so a person moving between
  // their laptop and their phone finds things in the same places.
  const all = [...orderFor(items, profile), settings];
  const overflow = all.length > MAX_TABS;
  const tabs = overflow ? all.slice(0, MAX_TABS) : all;
  const hidden = overflow ? all.slice(MAX_TABS) : [];

  const currentPath = (() => {
    for (const item of all) {
      if (item.href === "/") continue;
      if (matchPath(`${item.href}/*`, location.pathname)) return item.href;
    }
    return matchPath("/", location.pathname) ? "/" : false;
  })();

  const isPwa = window.matchMedia("(display-mode: standalone)").matches;
  const isWebiOS = /iPad|iPod|iPhone/.test(window.navigator.userAgent);
  const safeArea = isPwa && isWebiOS ? 15 : 0;

  return (
    <>
      <CreateButton bottomOffset={56 + safeArea} />

      <nav
        aria-label={translate("crm.navigation.label")}
        className="fixed bottom-0 left-0 right-0 z-50 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80"
        style={{ paddingBottom: safeArea }}
      >
        <div className="flex h-14 items-stretch justify-around">
          {tabs.map((item) => (
            <NavigationButton
              key={item.href}
              href={item.href}
              Icon={item.Icon}
              label={item.label}
              isActive={currentPath === item.href}
            />
          ))}

          {hidden.length > 0 ? (
            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              className={cn(
                "flex flex-1 flex-col items-center justify-center gap-0.5 text-muted-foreground",
                hidden.some((item) => item.href === currentPath) &&
                  "text-foreground",
              )}
            >
              <MoreHorizontal className="size-5" />
              <span className="text-[0.6rem] font-medium">More</span>
            </button>
          ) : null}
        </div>
      </nav>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="rounded-t-xl">
          <SheetHeader>
            <SheetTitle>More</SheetTitle>
          </SheetHeader>
          <div className="grid grid-cols-3 gap-2 p-4">
            {hidden.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setMoreOpen(false)}
                className={cn(
                  "flex flex-col items-center gap-2 rounded-lg border p-4 text-center",
                  currentPath === item.href
                    ? "border-primary bg-accent/40"
                    : "hover:bg-accent/40",
                )}
              >
                <item.Icon className="size-5" />
                <span className="text-xs font-medium">{item.label}</span>
              </Link>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

const NavigationButton = ({
  href,
  Icon,
  label,
  isActive,
}: {
  href: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  isActive: boolean;
}) => (
  <Link
    to={href}
    className={cn(
      "flex flex-1 flex-col items-center justify-center gap-0.5",
      isActive ? "text-foreground" : "text-muted-foreground",
    )}
  >
    <Icon className="size-5" />
    <span className="text-[0.6rem] font-medium truncate max-w-full px-1">
      {label}
    </span>
  </Link>
);

/**
 * Floating, bottom right, clear of the tab row.
 *
 * It used to sit in the middle of the bar as an oversized circle, which cost a
 * tab slot and read as a destination rather than an action.
 */
const CreateButton = ({ bottomOffset }: { bottomOffset: number }) => {
  const translate = useTranslate();
  const contact_id = useMatch("/contacts/:id/*")?.params.id;
  const [contactCreateOpen, setContactCreateOpen] = useState(false);
  const [noteCreateOpen, setNoteCreateOpen] = useState(false);
  const [taskCreateOpen, setTaskCreateOpen] = useState(false);

  const contacts = useCanAccess({ resource: "contacts", action: "create" });
  const notes = useCanAccess({ resource: "contact_notes", action: "create" });
  const tasks = useCanAccess({ resource: "tasks", action: "create" });

  const isPending = contacts.isPending || notes.isPending || tasks.isPending;
  const canCreate = contacts.canAccess || notes.canAccess || tasks.canAccess;

  if (isPending || !canCreate) return null;

  return (
    <>
      <ContactCreateSheet
        open={contactCreateOpen}
        onOpenChange={setContactCreateOpen}
      />
      <NoteCreateSheet
        open={noteCreateOpen}
        onOpenChange={setNoteCreateOpen}
        contact_id={contact_id}
      />
      <TaskCreateSheet
        open={taskCreateOpen}
        onOpenChange={setTaskCreateOpen}
        contact_id={contact_id}
      />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="icon"
            className="fixed right-4 z-50 size-14 rounded-full shadow-lg"
            style={{ bottom: bottomOffset + 16 }}
            aria-label={translate("ra.action.create")}
          >
            <Plus className="size-6" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" side="top">
          {contacts.canAccess ? (
            <DropdownMenuItem
              className="h-12 px-4 text-base"
              onSelect={() => setContactCreateOpen(true)}
            >
              {translate("resources.contacts.forcedCaseName")}
            </DropdownMenuItem>
          ) : null}
          {notes.canAccess ? (
            <DropdownMenuItem
              className="h-12 px-4 text-base"
              onSelect={() => setNoteCreateOpen(true)}
            >
              {translate("resources.notes.forcedCaseName")}
            </DropdownMenuItem>
          ) : null}
          {tasks.canAccess ? (
            <DropdownMenuItem
              className="h-12 px-4 text-base"
              onSelect={() => setTaskCreateOpen(true)}
            >
              {translate("resources.tasks.forcedCaseName")}
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
